import { ReactNode, useEffect, useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../shadcn/components/ui/accordion";
import CommercialIcon from "../../icons/CommercialIcon";
import IndustrialIcon from "../../icons/IndustrialIcon";
import ResidentialIcon from "../../icons/ResidentialIcon";
import { getSearchResults } from "services";

interface NavigationItems {
  title: string;
  value: string;
  icon: ReactNode;
  children?: { title: string; href: string; value: string }[];
}

const navigationItemsFallback: NavigationItems[] = [
  {
    title: "Residential",
    value: "residential",
    icon: <ResidentialIcon height="20px" width="20px" />,
    children: [
      {
        title: "Flats",
        href: "/residential-plot-residential-auction-properties?start_from=asset-type",
        value: "flats",
      },
      {
        title: "Individual House",
        href: "/residential-individual-house",
        value: "individual-house",
      },
      {
        title: "Bungalow",
        href: "/residential-bungalow",
        value: "bungalow",
      },
      {
        title: "Farm house",
        href: "/residential-farm-house",
        value: "farm-house",
      },
      {
        title: "Villa",
        href: "/residential-villa",
        value: "villa",
      },
    ],
  },
  {
    title: "Commercial",
    value: "commercial",
    icon: <CommercialIcon height="20px" width="20px" />,
    children: [
      {
        title: "Plot commercial",
        href: "/commercial-plot",
        value: "plot-commercial",
      },
      {
        title: "Mall",
        href: "/commercial-malls",
        value: "malls",
      },
      {
        title: "Office",
        href: "/commercial-office",
        value: "office",
      },
      {
        title: "Shops",
        href: "/commercial-shops",
        value: "shops",
      },
      {
        title: "Others",
        href: "/commercial-others",
        value: "others",
      },
    ],
  },
  {
    title: "Industrial",
    value: "industrial",
    icon: <IndustrialIcon height="20px" width="20px" />,
    children: [
      {
        title: "Godown",
        href: "/industrial-godown",
        value: "godown",
      },
      {
        title: "Plant & machinery",
        href: "/industrial-plant-machinery",
        value: "plant-machinery",
      },
      {
        title: "Goods",
        href: "/industrial-goods",
        value: "goods",
      },
      {
        title: "Plot industrial",
        href: "/industrial-plot",
        value: "plot-industrial",
      },
      {
        title: "Shed with plant & machinery",
        href: "/industrial-shed-with-plant-machinery",
        value: "shed-with-plant-machinery",
      },
      {
        title: "Industrial shed",
        href: "/industrial-shed",
        value: "industrial-shed",
      },
      {
        title: "Industrial land",
        href: "/industrial-land",
        value: "industrial-land",
      },
    ],
  },
  {
    title: "Others",
    value: "others",
    icon: <ResidentialIcon height="20px" width="20px" />,
  },
];

export function PropertiesMobile() {
  const [navigationItems, setNavigationItems] = useState<NavigationItems[]>(
    navigationItemsFallback
  );

  const init = async () => {
    const data: any = await getSearchResults({
      searches: [
        {
          collection: "properties",
          q: "*",
          query_by: "",
          facet_by: "dump.asset.sub_asset_type_slug",
          facet_return_parent: "dump.asset.sub_asset_type_slug",
          filter: "true",
          filter_by: `status:active`,
          max_facet_values: "500",
          per_page: 0,
        },
      ],
    });
    const filteredPropertiesData = data.results
      .flatMap(
        (result: any) =>
          (result?.facet_counts &&
            result?.facet_counts[0]?.counts.map((facet: any) => {
              return {
                title: facet?.parent?.asset_type_name,
                value: facet?.parent?.asset_type_slug,
                icon:
                  facet?.parent?.asset_type_slug === "commercial" ? (
                    <CommercialIcon height="20px" width="20px" />
                  ) : facet?.parent?.asset_type_slug === "industrial" ? (
                    <IndustrialIcon height="20px" width="20px" />
                  ) : (
                    <ResidentialIcon height="20px" width="20px" />
                  ),
                children: [
                  {
                    title: facet?.parent?.sub_asset_type_name,
                    href: `/${facet?.parent?.asset_type_slug}-${facet?.parent?.sub_asset_type_slug}-auction-properties`,
                    value: facet?.parent?.sub_asset_type_slug,
                  },
                ],
              };
            })) ||
          []
      )
      .reduce((acc: NavigationItems[], item: any) => {
        const existingCategory: any = acc.find(
          (cat) => cat.title === item.title
        );
        if (existingCategory) {
          existingCategory.children.push(...item.children);
        } else {
          acc.push(item);
        }
        return acc;
      }, [])
      .sort(
        (a: any, b: any) =>
          (a.value === "others" ? 1 : 0) - (b.value === "others" ? 1 : 0)
      );

    setNavigationItems(
      filteredPropertiesData.length > 0
        ? filteredPropertiesData
        : navigationItemsFallback
    );
  };

  useEffect(() => {
    (async () => {
      await init();
    })();
  }, []);
  return (
    <div>
      <Accordion type="single" collapsible className="w-full">
        {navigationItems.map((item, index) => (
          <AccordionItem
            key={`item-${index}`}
            value={item?.value}
            className="font-openSans font-normal text-mobile-subheading leading-4 text-brand-textBlack"
          >
            <AccordionTrigger>
              <div className="flex items-center h-[23px]">
                {item?.icon}
                <p className="ml-2 capitalize">{item?.title} </p>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <ul className="space-y-[25px]">
                {item?.children?.map((childItem: any) => {
                  return (
                    <li
                      key={childItem.value}
                      className="font-openSans capitalize text-web-body-md text-brand-textGrey font-normal leading-4"
                    >
                      <a href={childItem.href}>{childItem.title}</a>
                    </li>
                  );
                })}
              </ul>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
      {process.env.NEXT_PUBLIC_PYP_ENABLE === "true" && (
        <div
          onClick={() => {
            window.location.href = "/private-properties";
          }}
          className="flex items-center h-6 py-6 border-b border-[#FEECE6]"
        >
          <ResidentialIcon height="20px" width="20px" />
          <p className="font-openSans font-normal text-mobile-subheading leading-4 text-brand-textBlack ml-2 capitalize">
            Private{" "}
          </p>
        </div>
      )}
    </div>
  );
}
